/** Arcade initials entry (docs/spec/07 §2): three letters, up/down cycle, left/right move. Pure. */

import { wrapIndex } from './menuModel';

/** The letters on offer, in cycling order (the tables accept A-Z 0-9 space . - !). */
export const INITIAL_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789 .-!';

export interface InitialsState {
  /** Always three characters. */
  readonly letters: string;
  /** 0..2. */
  readonly cursor: number;
}

export function startInitials(previous = ''): InitialsState {
  const clean = [...previous.toUpperCase()].filter((c) => INITIAL_CHARS.includes(c)).join('');
  return { letters: (clean + 'AAA').slice(0, 3), cursor: 0 };
}

function setAt(letters: string, i: number, ch: string): string {
  return letters.slice(0, i) + ch + letters.slice(i + 1);
}

/** Up (+1) or down (-1) through the letters at the cursor, wrapping. */
export function cycleLetter(s: InitialsState, delta: number): InitialsState {
  const current = INITIAL_CHARS.indexOf(s.letters[s.cursor] ?? 'A');
  const next = INITIAL_CHARS[wrapIndex(Math.max(0, current) + delta, INITIAL_CHARS.length)] ?? 'A';
  return { ...s, letters: setAt(s.letters, s.cursor, next) };
}

export function moveCursor(s: InitialsState, delta: number): InitialsState {
  return { ...s, cursor: Math.max(0, Math.min(2, s.cursor + delta)) };
}

/** A typed character: put it at the cursor and move on. Characters not on offer are ignored (null). */
export function typeChar(s: InitialsState, ch: string): InitialsState | null {
  const up = ch.toUpperCase();
  if (up.length !== 1 || !INITIAL_CHARS.includes(up)) return null;
  return { letters: setAt(s.letters, s.cursor, up), cursor: Math.min(2, s.cursor + 1) };
}

/** Backspace: step back one letter. */
export function backspace(s: InitialsState): InitialsState {
  return moveCursor(s, -1);
}

/** Fire: move on a letter, or finish on the last. */
export function confirmLetter(s: InitialsState): { state: InitialsState; done: boolean } {
  if (s.cursor >= 2) return { state: s, done: true };
  return { state: moveCursor(s, 1), done: false };
}

/** What an input did to the editor: the new state, the sound to play, and whether entry is finished. */
export interface InitialsStep {
  readonly state: InitialsState;
  readonly sound: 'move' | 'type' | 'select' | 'error' | null;
  readonly done: boolean;
}

/** A menu action on the editor ('back' steps back a letter; there is no leaving without a name). */
export function initialsAction(s: InitialsState, a: string): InitialsStep {
  if (a === 'up' || a === 'down') return { state: cycleLetter(s, a === 'up' ? 1 : -1), sound: 'type', done: false };
  if (a === 'left' || a === 'right') {
    const next = moveCursor(s, a === 'left' ? -1 : 1);
    return { state: next, sound: next.cursor === s.cursor ? 'error' : 'move', done: false };
  }
  if (a === 'back') return { state: backspace(s), sound: s.cursor === 0 ? 'error' : 'move', done: false };
  if (a === 'confirm') {
    const { state, done } = confirmLetter(s);
    return { state, sound: done ? 'select' : 'move', done };
  }
  return { state: s, sound: null, done: false };
}

/**
 * A raw key: printable characters type (so WASD type letters here; the arrows still move) and
 * Backspace steps back. Null when the key is not for the editor and should become an action.
 */
export function initialsKey(s: InitialsState, code: string, key: string): InitialsStep | null {
  if (code === 'Backspace') return { state: backspace(s), sound: 'move', done: false };
  if (code === 'Space' || key.length !== 1) return null;
  const next = typeChar(s, key);
  return next ? { state: next, sound: 'type', done: false } : null;
}
